// ./src/pages/private/DashboardPage/DashboardPage.tsx
import React, { useCallback, useState, useEffect } from "react";
import { Stack, Group, SimpleGrid, Skeleton, Alert, Text } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { DashboardLayout } from "@/components/layouts";
import { AppButton, AppToolbar, AppIcon } from "@/components/generic";
import {
  WelcomeIntro,
  DashboardAlerts,
  DashboardPlanUsage,
  DashboardStats,
  DashboardRecentActivity,
  DashboardTopForms,
  DashboardOnboarding,
  SubmissionDetailsDrawer,
} from "@/components/features";
import { useDashboard, useForms, useSubmissions } from "@/hooks";
import { useApp } from "@/contexts";
import { DashboardActivityItem, Submission } from "@/utils/api";

export const DashboardPage: React.FC<PageProps> = (props: PageProps) => {
  const navigate = useNavigate();
  const { user } = useApp();

  // Submission drawer state
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<
    string | null
  >(null);
  const [drawerSubmission, setDrawerSubmission] = useState<Submission | null>(
    null
  );
  const [drawerLoading, setDrawerLoading] = useState(false);

  const {
    dashboard,
    loading,
    planUsage,
    stats,
    recentActivity,
    topForms,
    alerts,
    onboarding,
    generatedAt,
    refresh,
    lastError,
    clearError,
  } = useDashboard({
    autoLoad: true,
    userId: user?.id || null,
    activityLimit: 10,
    topFormsLimit: 5,
  });

  // Get form details for the submission drawer
  const { form: selectedForm, get: getForm } = useForms({
    autoLoad: false,
    isAuthenticated: !!user,
  });

  // Get submission details for the drawer
  const { submission: hookSubmission, get: getSubmission } = useSubmissions({
    autoLoad: false,
    isAuthenticated: !!user,
  });

  // Load submission when drawer opens
  useEffect(() => {
    if (selectedSubmissionId) {
      setDrawerLoading(true);
      getSubmission(selectedSubmissionId);
    } else {
      setDrawerSubmission(null);
    }
  }, [selectedSubmissionId, getSubmission]);

  // Sync drawer submission when hook's submission updates
  useEffect(() => {
    if (hookSubmission && hookSubmission.id === selectedSubmissionId) {
      setDrawerSubmission(hookSubmission);
      setDrawerLoading(false);
    }
  }, [hookSubmission, selectedSubmissionId]);

  // Navigation handlers
  const handleNavigateToForms = useCallback(() => {
    navigate("/forms");
  }, [navigate]);

  const handleNavigateToForm = useCallback(
    (formId: string) => {
      navigate(`/forms/${formId}/edit`);
    },
    [navigate]
  );

  const handleNavigateToSubmissions = useCallback(() => {
    navigate("/submissions");
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    clearError();
    refresh();
  }, [refresh, clearError]);

  const handleCreateForm = useCallback(() => {
    navigate("/forms/add");
  }, [navigate]);

  // Handle viewing submission in drawer
  const handleViewSubmission = useCallback(
    (item: DashboardActivityItem) => {
      setSelectedSubmissionId(item.id);

      // Load form details for the drawer
      if (item.formId) {
        getForm(item.formId);
      }
    },
    [getForm]
  );

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    setSelectedSubmissionId(null);
    setDrawerSubmission(null);
  }, []);

  // Show welcome intro for completely new users
  const isNewUser = onboarding?.showOnboarding && !onboarding?.hasCreatedForm;

  if (isNewUser && !loading) {
    return (
      <DashboardLayout {...props}>
        <WelcomeIntro />
      </DashboardLayout>
    );
  }

  // Loading skeleton
  if (loading && !dashboard) {
    return (
      <DashboardLayout {...props}>
        <Stack gap="lg">
          <Group justify="space-between">
            <Skeleton height={32} width={200} />
            <Skeleton height={36} width={100} />
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={120} radius="md" />
            ))}
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Skeleton height={300} radius="md" />
            <Skeleton height={300} radius="md" />
          </SimpleGrid>
        </Stack>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout {...props}>
      <Stack gap="lg">
        {/* Page header with refresh */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "status",
              items: [
                generatedAt ? (
                  <Text key="updated" size="sm" c="dimmed">
                    Last updated: {dayjs(generatedAt).format("MMM D, h:mm A")}
                  </Text>
                ) : null,
              ].filter(Boolean),
              align: "left",
            },
            {
              id: "actions",
              items: [
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={loading}
                  loading={loading}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Error alert */}
        {lastError && (
          <Alert
            color="red"
            title="Error loading dashboard"
            withCloseButton
            onClose={clearError}
            icon={<AppIcon iconName="AlertCircle" size={16} />}
          >
            {lastError}
          </Alert>
        )}

        {/* Alerts section */}
        <DashboardAlerts
          alerts={alerts}
          onNavigateToForm={handleNavigateToForm}
        />

        {/* Plan usage section */}
        {planUsage && <DashboardPlanUsage planUsage={planUsage} />}

        {/* Stats cards */}
        {stats && <DashboardStats stats={stats} />}

        {/* Two column layout for activity and top forms */}
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <DashboardRecentActivity
            recentActivity={recentActivity}
            onViewSubmission={handleViewSubmission}
            onNavigateToSubmissions={handleNavigateToSubmissions}
          />

          <DashboardTopForms
            topForms={topForms}
            onNavigateToForm={handleNavigateToForm}
            onNavigateToForms={handleNavigateToForms}
            onCreateForm={handleCreateForm}
          />
        </SimpleGrid>

        {/* Onboarding checklist */}
        {onboarding?.showOnboarding && onboarding.hasCreatedForm && (
          <DashboardOnboarding
            onboarding={onboarding}
            onNavigateToForms={handleNavigateToForms}
          />
        )}
      </Stack>

      {/* Submission details drawer */}
      <SubmissionDetailsDrawer
        opened={!!selectedSubmissionId}
        onClose={handleDrawerClose}
        submission={drawerSubmission}
        loading={drawerLoading}
        form={selectedForm}
        formFields={selectedForm?.formFields}
      />
    </DashboardLayout>
  );
};
